/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

/**
 * Security utilities for input sanitization and error handling.
 */

/**
 * Sanitizes error messages to prevent information disclosure.
 * @param error - The error object from API responses
 * @returns A safe, user-friendly error message
 */
export const sanitizeErrorMessage = (error: any): string => {
  // Define allowed error messages that are safe to display
  const allowedMessages = [
    'Invalid credentials',
    'Invalid email or password',
    'Account not found',
    'Account is disabled',
    'Too many login attempts',
    'Session expired',
    'Network error',
    'Server temporarily unavailable',
  ];

  // Extract error message from various possible error structures
  let errorMessage = '';
  if (error?.data?.detail) {
    errorMessage = error.data.detail;
  } else if (error?.data?.error) {
    errorMessage = error.data.error;
  } else if (error?.message) {
    errorMessage = error.message;
  }

  // Check if the error message is in our allowed list
  const isAllowedMessage = allowedMessages.some(allowed =>
    errorMessage.toLowerCase().includes(allowed.toLowerCase())
  );

  // Return sanitized message
  if (isAllowedMessage) {
    return errorMessage;
  }

  // Default safe message for any unrecognized errors
  return 'An error occurred. Please try again or contact support if the problem persists.';
};

/**
 * Sanitizes user input for display to prevent XSS attacks.
 * @param input - The user input string
 * @returns Sanitized string safe for display
 */
export const sanitizeDisplayText = (
  input: string | undefined | null
): string => {
  if (!input) {
    return '';
  }

  // Remove any HTML tags and encode special characters
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/[&]/g, '&amp;') // Encode ampersands
    .replace(/['"]/g, '') // Remove quotes
    .trim() // Remove leading/trailing whitespace
    .substring(0, 100); // Limit length to prevent overflow
};

/**
 * Validates that a string contains only safe characters for display.
 * @param input - The input string to validate
 * @returns True if the string is safe for display
 */
export const isDisplayTextSafe = (input: string): boolean => {
  // Allow letters, numbers, spaces, and basic punctuation
  const safePattern = /^[a-zA-Z0-9\s\-_.@]+$/;
  return safePattern.test(input) && input.length <= 100;
};

/**
 * Gets a safe user initial for avatar display.
 * @param firstName - User's first name
 * @param email - User's email as fallback
 * @returns A single uppercase letter safe for display
 */
export const getSafeUserInitial = (
  firstName?: string | null,
  email?: string | null
): string => {
  // Try first name first
  if (firstName && isDisplayTextSafe(firstName)) {
    return firstName.charAt(0).toUpperCase();
  }

  // Fallback to email
  if (email && isDisplayTextSafe(email)) {
    return email.charAt(0).toUpperCase();
  }

  // Safe default
  return 'U';
};

/**
 * Gets a safe user display name.
 * @param firstName - User's first name
 * @param email - User's email as fallback
 * @returns A sanitized display name
 */
export const getSafeDisplayName = (
  firstName?: string | null,
  email?: string | null
): string => {
  // Try first name first
  if (firstName) {
    const sanitized = sanitizeDisplayText(firstName);
    if (sanitized && isDisplayTextSafe(sanitized)) {
      return sanitized;
    }
  }

  // Fallback to email (first part only)
  if (email) {
    const emailPart = email.split('@')[0];
    const sanitized = sanitizeDisplayText(emailPart);
    if (sanitized && isDisplayTextSafe(sanitized)) {
      return sanitized;
    }
  }

  // Safe default
  return 'User';
};
